import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from "@angular/core";
import { UserType } from "@common/ADAPT.Common.Model/embed/user-type";
import { RoleInOrganisation } from "@common/ADAPT.Common.Model/organisation/connection";
import { ConnectionType } from "@common/ADAPT.Common.Model/organisation/connection-type";
import { DefaultRoleLabel, Role } from "@common/ADAPT.Common.Model/organisation/role";
import { IAddPeopleBindingModel, IAddPeopleConfig } from "@common/identity/add-people-binding-model.interface";
import { IUserDetailBindingModel } from "@common/identity/user-detail-binding-model.interface";
import { setInitialPasswordPageRoute } from "@common/identity/ux/set-initial-password-page/set-initial-password-page.route";
import { PromiseUtilities } from "@common/lib/utilities/promise-utilities";
import { RouteService } from "@common/route/route.service";
import { BaseComponent } from "@common/ux/base.component/base.component";
import { ResponsiveService } from "@common/ux/responsive/responsive.service";
import { OrganisationService } from "@org-common/lib/organisation/organisation.service";
import { UserManagementService } from "@org-common/lib/user-management/user-management.service";
import { WorkflowIdentifier } from "@org-common/lib/workflow/workflow-identifier.enum";
import { DxValidationGroupComponent } from "devextreme-angular";
import { personalDashboardPageRoute } from "../personal-dashboard-page/personal-dashboard-page.route";

export interface IAddPeople {
    firstName: string
    lastName: string;
    email: string;
    roleType: RoleInOrganisation;
}

@Component({
    selector: "adapt-add-people",
    templateUrl: "./add-people.component.html",
    standalone: false,
})
export class AddPeopleComponent extends BaseComponent implements OnInit {
    @Input() public config?: IAddPeopleConfig;

    @Output() public isValid = new EventEmitter<boolean>();
    @Output() public addPeopleBindingModelChanged = new EventEmitter<IAddPeopleBindingModel>();

    @ViewChild("validationGroup") private validationGroup?: DxValidationGroupComponent;

    public people: IAddPeople[] = [
        { firstName: "", lastName: "", email: "", roleType: RoleInOrganisation.Leader },
    ];

    public isMobile = false;

    private referenceData?: {
        welcomeRedirectUrl: string,
        passwordRedirectUrl: string,
        leaderAccessLevel: Role | undefined,
        adminAccessLevel: Role | undefined,
        viewerAccessLevel: Role | undefined,
    };

    public constructor(
        private organisationService: OrganisationService,
        private routeService: RouteService,
        private commonUserManagementService: UserManagementService,
        responsiveService: ResponsiveService,
    ) {
        super();

        responsiveService.currentBreakpoint$.pipe(
            this.takeUntilDestroyed(),
        ).subscribe((breakpoint) => this.isMobile = breakpoint.isMobileSize);
    }

    public async ngOnInit() {
        this.referenceData = await PromiseUtilities.fromObject({
            welcomeRedirectUrl: this.routeService.getControllerRoute(personalDashboardPageRoute.id),
            passwordRedirectUrl: this.routeService.getControllerRoute(setInitialPasswordPageRoute.id),
            leaderAccessLevel: this.commonUserManagementService.promiseToGetAccessLevelRoleWithLabel(UserType.Leader, DefaultRoleLabel.Leader),
            adminAccessLevel: this.commonUserManagementService.promiseToGetAccessLevelRoleWithLabel(UserType.Leader, DefaultRoleLabel.Administrator),
            viewerAccessLevel: this.commonUserManagementService.promiseToGetAccessLevelRoleWithLabel(UserType.Leader, DefaultRoleLabel.Viewer),
        });
    }

    public validateGroup() {
        this.isValid.next(!!this.validationGroup?.instance.validate().isValid);
        this.emitPeopleViewModel();
    }

    public addNewRow() {
        this.people.push({ firstName: "", lastName: "", email: "", roleType: RoleInOrganisation.Leader });
        setTimeout(() => {
            this.validateGroup();
        });
    }

    public removeLastRow() {
        this.people = this.people.slice(0, -1);
        setTimeout(() => {
            this.validateGroup();
        });
    }

    private emitPeopleViewModel() {
        const addPeopleViewModel: IAddPeopleBindingModel = {
            organisationId: this.organisationService.getOrganisationId(),
            setInitialPasswordRedirectUrl: this.setUrlBase(this.referenceData!.passwordRedirectUrl, true),
            welcomeRedirectUrl: this.setUrlBase(this.referenceData!.welcomeRedirectUrl),
            workflowId: WorkflowIdentifier.WelcomeOnboarding,
            teamId: this.config?.teamId,
            userDetails: this.people.map((p) => {

                const roleIds: number[] = [...(this.config?.roleIds ?? [])];
                if (p.roleType === RoleInOrganisation.Leader || p.roleType === RoleInOrganisation.OwnerLeader) {
                    if (this.referenceData?.leaderAccessLevel) {
                        roleIds.push(this.referenceData.leaderAccessLevel.roleId);
                    }

                    if (this.referenceData?.adminAccessLevel) {
                        roleIds.push(this.referenceData.adminAccessLevel.roleId);
                    }
                } else if (this.referenceData?.viewerAccessLevel) {
                    roleIds.push(this.referenceData.viewerAccessLevel.roleId);
                }

                return {
                    firstName: p.firstName,
                    lastName: p.lastName,
                    email: p.email,
                    roleIds,
                    hasAccess: true,
                    connectionType: ConnectionType.Employee,
                    userType: UserType.Leader,
                    roleInOrganisation: p.roleType,
                } as IUserDetailBindingModel;
            }),
        };

        this.addPeopleBindingModelChanged.emit(addPeopleViewModel);
    }

    private setUrlBase(url: string, stripQuery = false) {
        const parsedUrl = new URL(url, window.location.href);
        if (stripQuery) {
            parsedUrl.search = "";
        }
        return parsedUrl.href;
    }
}
